// Iter 538 — Panel HOLD & Pembatalan terpusat (Shopee only, Preview).
//
// Komponen utama:
//   • <HoldBadge order />         — badge status DIHOLD/MENUNGGU/DIBATALKAN di card
//   • <HoldActionsBar order />    — tombol "Ajukan HOLD" / "Ajukan Pembatalan" (semua PIC)
//   • <HoldManagerPage />         — halaman khusus Owner/Rido-approver (keputusan + riwayat)
//
// Backend endpoint:
//   POST /api/orders/{number}/hold/request    body { kind: "hold"|"cancel", reason }
//   POST /api/orders/{number}/hold/decide     body { decision: "acc_hold"|"acc_cancel"|"reject", reason }
//   POST /api/orders/{number}/hold/release    body { reason }
//   GET  /api/hold/pending                    list pengajuan menunggu keputusan
//   GET  /api/hold/active                     list order DIHOLD (acc_hold)
//   GET  /api/hold/canceled-internal          Riwayat Dibatalkan
//   GET  /api/orders/{number}/hold            detail hold + history
//
// Perilaku:
//   • Mandat Owner: aktivitas riwayat tetap lengkap; tidak menghapus data; tidak
//     menyentuh Shopee API; tidak mengubah progres asli. Semua guard di backend.
import React, { useState, useEffect, useCallback, useRef } from "react";
import { createPortal } from "react-dom";
import axios from "axios";
import { notify } from "./modal_center";
import { HoldJobEditHost } from "./shopee_module";
import { ChannelScope } from "./channel";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ---------------------------------------------------------------------------
// HoldBadge — ringkas di card. Prioritas: canceled → acc_hold → pending.
// ---------------------------------------------------------------------------
export function HoldBadge({ order }) {
  if (!order) return null;
  if (String(order.cancellation_status || "").toLowerCase() === "canceled_internal") {
    return (
      <span className="hold-badge hold-badge-canceled" data-testid="hold-badge-canceled">
        {order.cancel_source === "shopee_official" ? "DIBATALKAN SHOPEE" : "DIBATALKAN"}
      </span>
    );
  }
  const hold = order.hold || {};
  if (!hold.active) return null;
  const status = String(hold.status || "pending");
  if (status === "pending") {
    return (
      <span className="hold-badge hold-badge-pending" data-testid="hold-badge-pending" title={hold.special_reason || hold.reason || ""}>
        {hold.special_handling ? "BUTUH PENANGANAN KHUSUS" : "PERLU KLASIFIKASI ULANG"}
      </span>
    );
  }
  if (status === "acc_hold") {
    return (
      <span className="hold-badge hold-badge-held" data-testid="hold-badge-held" title={hold.reason || ""}>
        {holdSource(hold) === "shopee_request" ? "HOLD — MENUNGGU KEPUTUSAN SHOPEE" : "Ditahan"}
      </span>
    );
  }
  return null;
}

// ---------------------------------------------------------------------------
// HoldActionsBar — tombol "Ajukan HOLD" / "Ajukan Pembatalan" + modal alasan.
//   Tampil hanya untuk order Shopee aktif (bukan canceled/held).
// ---------------------------------------------------------------------------
export function HoldActionsBar({ order, onDone }) {
  const [showModal, setShowModal] = useState(null); // "hold" | "cancel" | null
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const lock = useRef(false);
  const submit = useCallback(async () => {
    if (!showModal || !order || lock.current) return;
    const r = (reason || "").trim();
    if (r.length < 3) { setErr("Alasan minimal 3 karakter"); return; }
    lock.current = true; setBusy(true); setErr("");
    try {
      const res = await axios.post(
        `${API}/orders/${encodeURIComponent(order.order_number)}/hold/request`,
        { kind: showModal, reason: r },
        { withCredentials: true },
      );
      notifySent(showModal, order.order_number, res.data);
      setShowModal(null); setReason("");
      if (onDone) onDone();
    } catch (e) {
      setErr(errText(e));
    } finally {
      lock.current = false; setBusy(false);
    }
  }, [showModal, reason, order, onDone]);

  if (!order) return null;
  const isShopee = String(order.source || "").toLowerCase() === "shopee";
  if (!isShopee) return null;
  const canceled = String(order.cancellation_status || "").toLowerCase() === "canceled_internal";
  const held = Boolean((order.hold || {}).active);
  // Order lama tanpa field hold → masih boleh ajukan. Yang di-block hanya canceled/held.
  const disabled = canceled || held;

  return (
    <HoldModalShared
      kind={showModal}
      reason={reason} setReason={setReason}
      busy={busy} err={err}
      orderNumber={order.order_number}
      customerName={order.customer_name}
      onCancel={() => { if (!busy) setShowModal(null); }}
      onSubmit={submit}
      renderTrigger={() => (
        <div className="hold-actions-bar" data-testid="hold-actions-bar">
          <button
            type="button"
            className="hold-btn hold-btn-hold"
            data-testid="hold-request-btn-hold"
            disabled={disabled}
            onClick={() => { setShowModal("hold"); setReason(""); setErr(""); }}
            title={disabled ? "Pesanan sudah dihold atau dibatalkan" : "HOLD pesanan ini (langsung berlaku)"}
          >
            HOLD Pesanan
          </button>
          <button
            type="button"
            className="hold-btn hold-btn-cancel"
            data-testid="hold-request-btn-cancel"
            disabled={disabled}
            onClick={() => { setShowModal("cancel"); setReason(""); setErr(""); }}
            title={disabled ? "Pesanan sudah dihold atau dibatalkan" : "Batalkan pesanan ini"}
          >
            Batalkan Pesanan
          </button>
        </div>
      )}
    />
  );
}

// Iter 541 — Kebab menu ringkas (dropdown "⋮") untuk Mockup Shopee / kartu Riwayat.
// Tampil kecil di sudut card, open menu "Ajukan HOLD" / "Ajukan Pembatalan".
// `orderNumber` + `customerName` wajib. `held`/`canceled` opsional (bila kartu
// sudah membawa info tsb, menu akan menolak klik). Mengirim ke endpoint yang
// SAMA dgn HoldActionsBar; backend otomatis route ke orders ATAU shopee_jobs.
// Satu menu ⋮ terbuka dalam satu waktu (kunci = ID pekerjaan unik).
let openMenuId = null;
const menuSubs = new Set();
const setOpenMenu = (id) => { openMenuId = id; menuSubs.forEach(f => f(id)); };
function useSingleOpen(id) {
  const [cur, setCur] = useState(openMenuId);
  useEffect(() => {
    menuSubs.add(setCur);
    return () => { menuSubs.delete(setCur); if (openMenuId === id) openMenuId = null; };
  }, [id]);
  return [cur === id, (v) => setOpenMenu(v ? id : (openMenuId === id ? null : openMenuId))];
}

// Posisi menu (fixed, di-portal ke body): di bawah tombol, naik ke atas bila mepet bawah, tetap di dalam layar.
const menuPos = (btn) => {
  const r = btn.getBoundingClientRect(), W = window.innerWidth, H = window.innerHeight, mw = 200, mh = 120;
  const left = Math.max(8, Math.min(r.right - mw, W - mw - 8));
  const top = r.bottom + 4 + mh > H ? Math.max(8, r.top - mh - 4) : r.bottom + 4;
  return { position: "fixed", left, top, zIndex: 2000 };
};

export function HoldKebabMenu({ orderNumber, customerName, held, canceled, onDone, menuId, testIdPrefix = "hold-kebab" }) {
  const [open, setOpen] = useSingleOpen(menuId || `${testIdPrefix}::${orderNumber}`);
  const btnRef = useRef(null);
  const menuRef = useRef(null);
  const [pos, setPos] = useState(null);
  useEffect(() => {
    if (!open) return;
    setPos(btnRef.current ? menuPos(btnRef.current) : null);
    const outside = (e) => { if (!menuRef.current?.contains(e.target) && !btnRef.current?.contains(e.target)) setOpen(false); };
    const esc = (e) => { if (e.key === "Escape") setOpen(false); };
    const close = () => setOpen(false);
    document.addEventListener("mousedown", outside); document.addEventListener("touchstart", outside);
    document.addEventListener("keydown", esc); window.addEventListener("scroll", close, true); window.addEventListener("resize", close);
    return () => {
      document.removeEventListener("mousedown", outside); document.removeEventListener("touchstart", outside);
      document.removeEventListener("keydown", esc); window.removeEventListener("scroll", close, true); window.removeEventListener("resize", close);
    };
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps
  const [showModal, setShowModal] = useState(null); // "hold" | "cancel" | null
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const lock = useRef(false);
  const submit = useCallback(async () => {
    if (!showModal || lock.current) return;
    const r = (reason || "").trim();
    if (r.length < 3) { setErr("Alasan minimal 3 karakter"); return; }
    lock.current = true; setBusy(true); setErr("");
    try {
      const res = await axios.post(
        `${API}/orders/${encodeURIComponent(orderNumber)}/hold/request`,
        { kind: showModal, reason: r },
        { withCredentials: true },
      );
      notifySent(showModal, orderNumber, res.data);
      setShowModal(null); setReason(""); setOpen(false);
      if (onDone) onDone();
    } catch (e) {
      setErr(errText(e));
    } finally {
      lock.current = false; setBusy(false);
    }
  }, [showModal, reason, orderNumber, onDone]);

  const disabled = Boolean(held) || Boolean(canceled);
  // Hanya hentikan bubbling ke kartu (onClick buka detail / Space-Enter). JANGAN preventDefault:
  // itu memblokir ketikan di textarea modal yang event-nya ikut bubble lewat pohon React.
  const stop = (e) => { e.stopPropagation(); };

  return (
    <span className="hold-kebab-wrap" style={{ position: "relative", display: "inline-flex" }} onClick={stop} onKeyDown={stop}>
      <button
        type="button"
        className="hold-kebab-btn"
        data-testid={`${testIdPrefix}-trigger-${orderNumber}`}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label="Menu HOLD / Pembatalan"
        title="Menu HOLD / Pembatalan"
        ref={btnRef}
        onClick={(e) => { e.stopPropagation(); e.preventDefault(); setOpen(!open); }}
      >
        ⋮
      </button>
      {open && createPortal(
        <div className="hold-kebab-menu" role="menu" data-testid={`${testIdPrefix}-menu-${orderNumber}`} ref={menuRef}
             style={pos || { position: "fixed", visibility: "hidden" }} onClick={stop} onKeyDown={stop}>
          <button
            type="button" role="menuitem"
            className="hold-kebab-item"
            data-testid={`${testIdPrefix}-item-hold-${orderNumber}`}
            disabled={disabled}
            onClick={(e) => { e.stopPropagation(); e.preventDefault(); setOpen(false); setShowModal("hold"); setReason(""); setErr(""); }}
          >
            HOLD Pesanan
          </button>
          <button
            type="button" role="menuitem"
            className="hold-kebab-item"
            data-testid={`${testIdPrefix}-item-cancel-${orderNumber}`}
            disabled={disabled}
            onClick={(e) => { e.stopPropagation(); e.preventDefault(); setOpen(false); setShowModal("cancel"); setReason(""); setErr(""); }}
          >
            Batalkan Pesanan
          </button>
          {disabled && (
            <p className="muted small" style={{ padding: "4px 8px", margin: 0 }}>
              {canceled ? "Pesanan sudah dibatalkan" : "Pesanan sudah dihold"}
            </p>
          )}
        </div>,
        document.body,
      )}
      <HoldModalShared
        kind={showModal}
        reason={reason} setReason={setReason}
        busy={busy} err={err}
        orderNumber={orderNumber}
        customerName={customerName}
        onCancel={() => { if (!busy) setShowModal(null); }}
        onSubmit={submit}
      />
    </span>
  );
}

const errText = (e) => {
  const d = e?.response?.data?.detail;
  if (typeof d === "string") return d;
  if (!e?.response) return "Koneksi terputus — periksa internet lalu tekan kirim lagi. Teks alasan tidak hilang.";
  return `Gagal mengirim pengajuan (kode ${e.response.status}).`;
};
// internal | shopee_request (pembeli mengajukan batal, menunggu keputusan Shopee) | shopee_official
export const holdSource = (h) => {
  if (!h) return "internal";
  if (h.source) return h.source;
  if (h.cancel_source === "shopee_official" || h.requested_by?.kind === "system") return h.kind === "hold" ? "shopee_request" : "shopee_official";
  return "internal";
};

export const OUTCOME_TEXT = {
  held: ["Pesanan di-HOLD", "HOLD langsung berlaku. Pekerjaan terkunci sampai HOLD dibuka."],
  canceled: ["Pesanan dibatalkan", "Mockup belum pernah dicetak — pembatalan internal langsung berlaku."],
  special: ["Perlu Pemeriksaan", "Mockup sudah dicetak / sudah produksi — pekerjaan dikunci, pembatalan diputuskan Tim Mockup atau Owner/Admin."],
};
const notifySent = (kind, orderNumber, data) => {
  const [title, msg] = OUTCOME_TEXT[data?.outcome] || OUTCOME_TEXT[kind === "hold" ? "held" : "special"];
  notify.success({ title, message: `${orderNumber} — ${msg}` });
};

// Shared modal (dipakai HoldActionsBar + HoldKebabMenu). Tidak mengubah styling existing.
function HoldModalShared({ kind, reason, setReason, busy, err, orderNumber, customerName, onCancel, onSubmit, renderTrigger }) {
  return (
    <>
      {renderTrigger && renderTrigger()}
      {kind && createPortal(
        // Portal ke <body>: lepas dari kartu (overflow:hidden / transform) yang membuat modal terpotong & tidak bisa diklik.
        <div className="hold-modal-overlay" role="dialog" aria-modal="true" data-testid="hold-request-modal"
             onClick={(e) => { if (e.target === e.currentTarget) onCancel(); }}
             onKeyDown={(e) => { if (e.key === "Escape") onCancel(); }}>
          <div className="hold-modal">
            <h3>{kind === "hold" ? "HOLD Pesanan" : "Batalkan Pesanan"}</h3>
            <p className="muted small">
              Nota: <strong>{orderNumber}</strong>{customerName ? ` · ${customerName}` : ""}
            </p>
            {kind === "cancel" && <div className="hold-modal-err" role="note" data-testid="hold-cancel-revision-warning">
              Jika hanya revisi desain atau keterangan, gunakan Revisi/HOLD, bukan Batalkan Pesanan.</div>}
            <label className="hold-modal-label">Alasan (wajib, min 3 karakter)</label>
            <textarea
              data-testid="hold-request-reason"
              autoFocus
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              readOnly={busy}
              rows={4}
              placeholder={kind === "hold" ? "Mis: menunggu klarifikasi warna dari pelanggan" : "Mis: pelanggan batal, pesanan tidak dilanjutkan"}
            />
            {err && <div className="hold-modal-err" data-testid="hold-request-err">{err}</div>}
            <p className="muted small hold-modal-note">
              <strong>Catatan:</strong> Langsung berlaku tanpa ACC. HOLD mengunci pekerjaan di semua tahap sampai dibuka. Pembatalan langsung berlaku bila mockup belum pernah dicetak; bila sudah dicetak/produksi, pekerjaan dikunci dan menjadi "Perlu Pemeriksaan".
              Pekerjaan fisik di workshop <strong>harus Anda hentikan sendiri</strong> — penguncian aplikasi tidak otomatis menghentikan mesin.
            </p>
            <div className="hold-modal-actions">
              <button type="button" className="soft-btn" onClick={onCancel} disabled={busy} data-testid="hold-request-cancel-btn">
                Batal
              </button>
              <button
                type="button"
                className="primary-btn"
                onClick={onSubmit}
                disabled={busy || (reason || "").trim().length < 3}
                data-testid="hold-request-submit-btn"
              >
                {busy ? "Mengirim…" : (kind === "hold" ? "HOLD Sekarang" : "Batalkan Sekarang")}
              </button>
            </div>
          </div>
        </div>,
        document.body,
      )}
    </>
  );
}

// ---------------------------------------------------------------------------
// HoldManagerPage — Owner / Rido-approver.
//   3 bagian: Pending (perlu keputusan), Aktif (DIHOLD), Riwayat Dibatalkan.
// ---------------------------------------------------------------------------
export const HOLD_TEAMS = ["Shopee", "Tokopedia", "Shopee + Tokopedia"];

// Filter tampilan Owner/Admin: kanal yang dilihat (data & status pengajuan tidak berubah).
const HOLD_CH = [["shopee", "Shopee"], ["tokopedia", "Tokopedia"], ["offline", "Offline"]];

function HoldChannelTabs({ ch, setCh }) {
  return <div className="hold-ch-tabs" role="tablist" data-testid="hold-channel-tabs" style={{ display: "flex", gap: 6, margin: "4px 0 10px" }}>
    {HOLD_CH.map(([k, l]) => <button key={k} type="button" role="tab" aria-selected={ch === k} className={`btn ${ch === k ? "primary" : "ghost"}`}
      onClick={() => setCh(k)} data-testid={`hold-channel-${k}`}>{l}</button>)}
  </div>;
}

export function HoldManagerPage({ me, channelPicker = false, initialChannel }) {
  // Tim Mockup (pekerjaannya sendiri) & Owner/Admin (semua). HOLD/BATAL belum dicetak berlaku otomatis — tanpa antrean ACC.
  const isSup = ["owner", "admin"].includes(String(me?.role || "").toLowerCase());
  const isTeam = HOLD_TEAMS.includes(me?.team);
  const canApprove = isTeam || isSup;
  const picker = channelPicker && isSup;
  const [ch, setCh] = useState(HOLD_CH.some(([k]) => k === initialChannel) ? initialChannel : "shopee");

  const [pending, setPending] = useState([]);
  const special = pending.filter(o => o.special_handling);
  const legacy = pending.filter(o => !o.special_handling);
  const [active, setActive] = useState([]);
  const [canceled, setCanceled] = useState([]);
  const [err, setErr] = useState("");
  const [busyKey, setBusyKey] = useState("");

  const load = useCallback(async () => {
    setErr("");
    if (picker && ch === "offline") { setPending([]); setActive([]); setCanceled([]); return; }
    // Kanal Tokopedia: hanya pekerjaan Mockup kanal Tokopedia (orders source=shopee adalah pesanan Shopee).
    const keep = (xs) => (picker && ch === "tokopedia" ? (xs || []).filter(o => o._scope === "shopee_jobs") : xs || []);
    try {
      const [p, a, c] = await Promise.all([
        axios.get(`${API}/hold/pending`, { withCredentials: true }),
        axios.get(`${API}/hold/active`, { withCredentials: true }),
        axios.get(`${API}/hold/canceled-internal`, { withCredentials: true }),
      ]);
      setPending(keep(p.data)); setActive(keep(a.data)); setCanceled(keep(c.data));
    } catch (e) {
      setErr(e?.response?.data?.detail || "Gagal memuat data");
    }
  }, [picker, ch]);

  useEffect(() => { if (canApprove) load(); }, [canApprove, load]);

  const decide = async (num, decision, reason) => {
    setBusyKey(`${num}-${decision}`);
    try {
      await axios.post(
        `${API}/orders/${encodeURIComponent(num)}/hold/decide`,
        { decision, reason },
        { withCredentials: true },
      );
      await load();
    } catch (e) {
      setErr(e?.response?.data?.detail || "Gagal memproses keputusan");
    } finally {
      setBusyKey("");
    }
  };

  const reclassify = async (num) => {
    if (!window.confirm(`Klasifikasi ulang pengajuan lama ${num}? Sistem memeriksa riwayat cetak/produksi lalu menerapkan aturan baru.`)) return;
    setBusyKey(`${num}-reclassify`);
    try {
      const res = await axios.post(`${API}/orders/${encodeURIComponent(num)}/hold/reclassify`, {}, { withCredentials: true });
      const [t, m] = OUTCOME_TEXT[res.data.outcome] || ["Selesai", ""];
      notify.success({ title: t, message: `${num} — ${m}` });
      await load();
    } catch (e) {
      setErr(e?.response?.data?.detail || "Gagal klasifikasi ulang");
    } finally {
      setBusyKey("");
    }
  };

  const release = async (num, reason) => {
    setBusyKey(`${num}-release`);
    try {
      await axios.post(
        `${API}/orders/${encodeURIComponent(num)}/hold/release`,
        { reason },
        { withCredentials: true },
      );
      await load();
      return null;
    } catch (e) {
      const m = e?.response?.data?.detail || "Gagal melanjutkan pekerjaan. Coba lagi.";
      setErr(m);
      return typeof m === "string" ? m : "Gagal melanjutkan pekerjaan. Coba lagi.";
    } finally {
      setBusyKey("");
    }
  };

  if (!canApprove) {
    return (
      <div className="hold-manager hold-manager-forbidden" data-testid="hold-manager-forbidden">
        <h2>HOLD / Pembatalan Shopee</h2>
        <p className="muted">
          Hanya Tim Mockup (Shopee/Tokopedia) dan Owner/Admin yang dapat membuka halaman ini.
        </p>
      </div>
    );
  }

  return (
    <div className="hold-manager" data-testid="hold-manager">
      {picker && <ChannelScope ch={ch === "tokopedia" ? "tokopedia" : "shopee"}/>}
      <h2>HOLD / Pembatalan {picker ? HOLD_CH.find(([k]) => k === ch)[1] : "Shopee"}</h2>
      {picker && <HoldChannelTabs ch={ch} setCh={setCh}/>}
      {err && <div className="hold-manager-err" data-testid="hold-manager-err">{err}</div>}
      {picker && ch === "offline" ? <p className="muted" data-testid="hold-offline-note">Fitur Tahan/Batal hanya berlaku untuk pesanan online (Shopee dan Tokopedia). Pesanan Offline tidak memakai fitur ini.</p> : <>

      <p className="muted small" data-testid="hold-auto-note">HOLD dan pembatalan pesanan yang mockup-nya belum pernah dicetak berlaku otomatis — tidak ada antrean ACC.
        {!isSup && " Anda hanya melihat pesanan yang pernah Anda tangani."}</p>
      <section data-testid="hold-pending-section">
        <h3>Perlu Pemeriksaan ({special.length})</h3>
        {special.length === 0 ? <p className="muted small">Tidak ada.</p> : (
          <ul className="hold-pending-list">
            {special.map((o) => <HoldPendingItem key={o.order_number} order={o} onDecide={decide} busyKey={busyKey} />)}
          </ul>
        )}
      </section>
      {legacy.length > 0 && <section data-testid="hold-reclassify-section">
        <h3>Perlu Klasifikasi Ulang — pengajuan lama ({legacy.length})</h3>
        <ul className="hold-pending-list">
          {legacy.map((o) => <ReclassifyItem key={o.order_number} order={o} canDo={isSup} onDo={reclassify} busyKey={busyKey} />)}
        </ul>
      </section>}

      <section data-testid="hold-active-section">
        <h3>Pesanan Ditahan Sementara ({active.length})</h3>
        {active.length === 0 ? (
          <p className="muted small">Tidak ada pesanan yang ditahan.</p>
        ) : (
          <ul className="hold-active-list">
            {active.map((o) => (
              <HoldActiveItem key={o.order_number} order={o} onRelease={release} busyKey={busyKey} canRelease={isTeam || isSup} onEdited={load} />
            ))}
          </ul>
        )}
      </section>

      <section data-testid="hold-canceled-section">
        <h3>Pesanan Dibatalkan ({canceled.length})</h3>
        {canceled.length === 0 ? (
          <p className="muted small">Belum ada pesanan dibatalkan.</p>
        ) : (
          <ul className="hold-canceled-list">
            {canceled.map((o) => (
              <li key={o.order_number} className="hold-item hold-item-canceled" data-testid={`hold-canceled-${o.order_number}`}>
                <div className="hold-item-head">
                  <strong>{o.order_number}</strong> · {o.customer_name || "-"}
                  <span className="hold-badge hold-badge-canceled" data-testid={`hold-canceled-source-${o.order_number}`}>{o.cancel_source === "shopee_official" ? "DIBATALKAN SHOPEE" : "BATAL INTERNAL"}</span>
                </div>
                <p className="muted small">
                  Dibatalkan oleh: <strong>{o.canceled_internal?.by?.name || "-"}</strong> ·{" "}
                  {o.canceled_internal?.at || ""}
                </p>
                <p className="small">Alasan: {o.canceled_internal?.reason || "-"}</p>
                <p className="small muted">Pengaju awal: {o.canceled_internal?.requested_by?.name || "-"} — "{o.canceled_internal?.original_reason || "-"}"</p>
                {isSup && o._scope === "shopee_jobs" && o.cancel_source !== "shopee_official" && <RestoreCancel num={o.order_number} onDone={load}/>}
              </li>
            ))}
          </ul>
        )}
      </section>
      </>}
    </div>
  );
}

function HoldPendingItem({ order, onDecide, busyKey }) {
  const [reason, setReason] = useState("");
  const num = order.order_number;
  const h = order.hold || {};
  const prodSummary = (order.products || []).map(p => `${p.custom_name || p.name} ×${p.quantity || 0}`).slice(0, 3).join(", ");
  return (
    <li className="hold-item hold-item-pending" data-testid={`hold-pending-${num}`}>
      <div className="hold-item-head">
        <strong>{num}</strong> · {order.customer_name || "-"}
        <span className="hold-badge hold-badge-pending">PENANGANAN KHUSUS · {h.kind === "hold" ? "HOLD" : "BATAL"}</span>
      </div>
      <p className="small" data-testid={`hold-special-reason-${num}`}>Alasan khusus: <strong>{h.special_reason || "-"}</strong> — pekerjaan sudah dikunci.</p>
      <p className="muted small">Produk: {prodSummary || "-"}</p>
      <p className="small">Pengaju: <strong>{h.requested_by?.name || "-"}</strong> · {h.requested_at || ""}</p>
      <p className="small">Alasan pengaju: <em>{h.reason || "-"}</em></p>
      <div className="hold-item-actions">
        <input
          type="text"
          placeholder="Alasan keputusan (wajib untuk Batalkan / Lanjutkan)"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          data-testid={`hold-decide-reason-${num}`}
        />
        <button
          type="button"
          className="hold-btn hold-btn-acc-hold"
          onClick={() => onDecide(num, "acc_hold", reason || "ACC HOLD")}
          disabled={!!busyKey}
          data-testid={`hold-decide-acc-hold-${num}`}
        >
          Tetap HOLD
        </button>
        <button
          type="button"
          className="hold-btn hold-btn-acc-cancel"
          onClick={() => onDecide(num, "acc_cancel", reason)}
          disabled={!!busyKey || reason.trim().length < 3}
          data-testid={`hold-decide-acc-cancel-${num}`}
          title={reason.trim().length < 3 ? "Isi alasan dulu" : "ACC Pembatalan (final operasional)"}
        >
          Batalkan Pesanan
        </button>
        {holdSource(h) === "internal" && <button
          type="button"
          className="hold-btn hold-btn-reject"
          onClick={() => onDecide(num, "reject", reason)}
          disabled={!!busyKey || reason.trim().length < 3}
          data-testid={`hold-decide-reject-${num}`}
          title={reason.trim().length < 3 ? "Isi alasan dulu" : "Tolak pengajuan, pekerjaan lanjut"}
        >
          Lanjutkan Pekerjaan
        </button>}
      </div>
    </li>
  );
}

function ReclassifyItem({ order, canDo, onDo, busyKey }) {
  const num = order.order_number, h = order.hold || {};
  const sug = { otomatis_hold: "HOLD langsung berlaku", otomatis_batal: "Batal otomatis (belum pernah dicetak)", penanganan_khusus: "Perlu Pemeriksaan (sudah dicetak/produksi)" }[order.suggestion] || "-";
  return (
    <li className="hold-item hold-item-pending" data-testid={`hold-reclassify-${num}`}>
      <div className="hold-item-head"><strong>{num}</strong> · {order.customer_name || "-"}
        <span className="hold-badge hold-badge-pending">PERLU KLASIFIKASI ULANG · {h.kind === "hold" ? "HOLD" : "BATAL"}</span></div>
      <p className="small">Pengaju: <strong>{h.requested_by?.name || "-"}</strong> · {h.requested_at || ""} · Alasan: <em>{h.reason || "-"}</em></p>
      <p className="small" data-testid={`hold-reclassify-suggestion-${num}`}>Usulan sistem: <strong>{sug}</strong></p>
      {canDo ? <button type="button" className="hold-btn hold-btn-acc-hold" disabled={!!busyKey} onClick={() => onDo(num)} data-testid={`hold-reclassify-btn-${num}`}>Klasifikasi Ulang</button>
        : <p className="muted small">Menunggu klasifikasi ulang oleh Owner/Admin.</p>}
    </li>
  );
}

// "22 Okt 2026 · 03.38 WIB"
export const fmtWIB = (iso) => {
  const d = iso ? new Date(iso) : null;
  if (!d || isNaN(d)) return iso || "-";
  const o = { timeZone: "Asia/Jakarta" };
  return `${d.toLocaleDateString("id-ID", { ...o, day: "numeric", month: "short", year: "numeric" })} · ${d.toLocaleTimeString("id-ID", { ...o, hour: "2-digit", minute: "2-digit" })} WIB`;
};

function HoldActiveItem({ order, onRelease, busyKey, canRelease, onEdited }) {
  const [confirm, setConfirm] = useState(false);
  const [edit, setEdit] = useState("");  // "" | "edit" | "mockup"
  const num = order.order_number;
  const h = order.hold || {};
  const internal = holdSource(h) === "internal";
  const canEdit = canRelease && internal && !!order.job_id;
  const Row = ({ k, children, tid }) => <div className="hold-kv"><span>{k}</span><b data-testid={tid}>{children}</b></div>;
  return (
    <li className="hold-item hold-item-active" data-testid={`hold-active-${num}`}>
      <div className="hold-kv-list">
        <Row k="Nomor" tid={`hold-active-num-${num}`}>{num}</Row>
        <Row k="Pelanggan">{order.customer_name || "-"}</Row>
        <Row k="Status"><span className="hold-badge hold-badge-held">Ditahan</span></Row>
        <Row k="Alasan" tid={`hold-active-reason-${num}`}>{h.reason || "-"}</Row>
        <Row k="Ditahan oleh">{h.decided_by?.name || h.requested_by?.name || "-"}</Row>
        <Row k="Waktu" tid={`hold-active-time-${num}`}>{fmtWIB(h.decided_at || h.requested_at)}</Row>
      </div>
      {!internal && <p className="small hold-item-note" data-testid={`hold-shopee-locked-${num}`}>Ditahan oleh Shopee — tidak bisa dilanjutkan manual. Terbuka otomatis bila Shopee menolak pengajuan batal.</p>}
      {canRelease && internal && <div className="hold-item-actions hold-item-actions-compact">
        {canEdit && <button type="button" className="hold-btn hold-btn-soft" onClick={() => setEdit("edit")} disabled={!!busyKey}
          data-testid={`hold-edit-job-btn-${num}`}>Ubah Detail Pesanan</button>}
        {canEdit && <button type="button" className="hold-btn hold-btn-soft" onClick={() => setEdit("mockup")} disabled={!!busyKey}
          data-testid={`hold-edit-mockup-btn-${num}`}>Ganti Desain</button>}
        <button type="button" className="hold-btn hold-btn-release hold-btn-main" onClick={() => setConfirm(true)} disabled={!!busyKey}
          data-testid={`hold-release-open-${num}`}>Lanjutkan Pekerjaan</button>
      </div>}
      {confirm && <ResumeDialog num={num} customer={order.customer_name} busy={busyKey === `${num}-release`}
        onBack={() => setConfirm(false)} onConfirm={async (r) => { const e = await onRelease(num, r); if (!e) setConfirm(false); return e; }}/>}
      {edit && <HoldJobEditHost jobId={order.job_id} mode={edit} onClose={(changed) => { setEdit(""); if (changed) onEdited?.(); }}/>}
    </li>
  );
}

// Konfirmasi Lanjutkan Pekerjaan (= buka HOLD internal). HOLD baru dibuka setelah server menyetujui.
function ResumeDialog({ num, customer, busy, onBack, onConfirm }) {
  const [reason, setReason] = useState("");
  const [err, setErr] = useState("");
  const ok = reason.trim().length >= 3 && !busy;
  const submit = async () => { if (!ok) return; setErr(""); const e = await onConfirm(reason.trim()); if (e) setErr(e); };
  return createPortal(
    <div className="hold-modal-overlay" role="dialog" aria-modal="true" data-testid={`hold-release-form-${num}`}
         onClick={(e) => { if (e.target === e.currentTarget && !busy) onBack(); }}
         onKeyDown={(e) => { if (e.key === "Escape" && !busy) onBack(); }}>
      <div className="hold-modal">
        <h3>Lanjutkan Pekerjaan Ini?</h3>
        <p className="muted small">Nomor: <strong>{num}</strong>{customer ? ` · ${customer}` : ""}</p>
        <p className="small">Pastikan masalah pada pesanan sudah diselesaikan sebelum pekerjaan dilanjutkan.</p>
        <label className="hold-modal-label" htmlFor={`resume-${num}`}>Alasan melanjutkan pekerjaan (wajib)</label>
        <input id={`resume-${num}`} type="text" autoFocus value={reason} readOnly={busy} onChange={(e) => setReason(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") submit(); }} placeholder="Mis: revisi desain sudah selesai"
          data-testid={`hold-release-reason-${num}`} />
        {err && <div className="hold-modal-err" data-testid={`hold-release-err-${num}`}>{err}</div>}
        <div className="hold-modal-actions">
          <button type="button" className="soft-btn" onClick={onBack} disabled={busy} data-testid={`hold-release-cancel-${num}`}>Kembali</button>
          <button type="button" className="primary-btn" onClick={submit} disabled={!ok} data-testid={`hold-release-btn-${num}`}>
            {busy ? "Memproses…" : "Ya, Lanjutkan"}</button>
        </div>
      </div>
    </div>,
    document.body,
  );
}

// Owner/Admin: pulihkan BATAL internal — server memeriksa status resmi Shopee terbaru dulu.
function RestoreCancel({ num, onDone }) {
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");
  const go = async () => {
    if (busy) return;
    if (reason.trim().length < 3) { setMsg("Alasan minimal 3 karakter"); return; }
    setBusy(true); setMsg("");
    try {
      await axios.post(`${API}/orders/${encodeURIComponent(num)}/cancel/restore`, { reason: reason.trim() }, { withCredentials: true });
      notify.success({ title: "Pesanan dipulihkan", message: `${num} — Shopee masih aktif, pekerjaan kembali ke tahap terakhirnya.` });
      if (onDone) await onDone();
    } catch (e) {
      setMsg(errText(e));
    } finally { setBusy(false); }
  };
  return <div className="hold-item-actions" data-testid={`hold-restore-${num}`}>
    <input type="text" value={reason} onChange={e => setReason(e.target.value)} placeholder="Alasan pemulihan (mis. hanya revisi)"
      readOnly={busy} data-testid={`hold-restore-reason-${num}`}/>
    <button type="button" className="soft-btn" disabled={busy} onClick={go} data-testid={`hold-restore-btn-${num}`}>
      {busy ? "Mengecek Shopee…" : "Pulihkan (Cek Shopee)"}</button>
    {msg && <div className="hold-modal-err" data-testid={`hold-restore-err-${num}`}>{msg}</div>}
  </div>;
}
